import React, { useEffect, useState } from 'react';
import { getMyProducts, updateProductPrice } from '@/services/productService';
import { formatCurrency } from '@/utils/format';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2, Clock, Calculator, Check, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as Dialog from '@radix-ui/react-dialog';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'react-hot-toast';

interface Product {
  id: string;
  productName: string;
  ean: string;
  proposedPrice: number;
  stockQuantity: number;
  desiredChannel: string;
  commission: number;
  valueToReceive: number;
  ptPrice?: number;
  buyboxPrice?: number;
  isActive: boolean;
  status: string;
  asin?: string;
  mainImage?: string;
  minimumPrice?: number;
  commissionVAT?: number;
  esPrice?: number;
  lastPriceSync?: Date;
  lastSyncedPrice?: number;
  publishedAt?: Date;
  publishedDescription?: string;
  publishedTitle?: string;
  publishedImageUrl?: string;
  recommendedPrice?: number;
  reviewedAt?: Date;
  approvedAt?: Date;
  submittedAt?: Date;
  submittedBy: string;
  submitterName: string;
  adminNotes?: string;
  sku: string;
  totalOffers?: number;
}

// Interface para produtos agrupados por EAN
interface GroupedProduct {
  ean: string;
  productName: string;
  channels: {
    [channel: string]: Product;
  };
  expanded: boolean;
}

const getMarketplaceLogo = (channel: string) => {
  const channelLowerCase = channel.toLowerCase();
  switch (channelLowerCase) {
    case 'amazon':
      return '/images/amazon.png';
    case 'fnac':
      return '/images/fnac.png';
    case 'worten':
      return '/images/worten.png';
    case 'kuantokusta':
      return '/images/kuantokusta.png';
    default:
      return '';
  }
};

export default function MyProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [groupedProducts, setGroupedProducts] = useState<GroupedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [sellingPrice, setSellingPrice] = useState('');
  const [currentValueToReceive, setCurrentValueToReceive] = useState<number>(0);
  const [newMinPrice, setNewMinPrice] = useState('');
  const [newProposedPrice, setNewProposedPrice] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const { currentUser } = useAuth();

  // Função para calcular comissão corretamente
  const calculateCommission = (price: number) => {
    const priceWithoutVAT = price / 1.23; // Remove IVA do preço
    const commission = priceWithoutVAT * 0.20; // 20% do preço sem IVA
    const ivaOnCommission = commission * 0.23; // 23% de IVA sobre a comissão
    const finalValue = price - commission - ivaOnCommission;
    
    return {
      commission,
      iva: ivaOnCommission,
      finalValue
    };
  };

  useEffect(() => {
    loadProducts();
  }, [currentUser]);

  const loadProducts = async () => {
    try {
      const data = await getMyProducts();
      setProducts(data);
      
      // Agrupar produtos por EAN
      const groupedData: { [ean: string]: GroupedProduct } = {};
      
      data.forEach(product => {
        if (!groupedData[product.ean]) {
          groupedData[product.ean] = {
            ean: product.ean,
            productName: product.productName,
            channels: {},
            expanded: false
          };
        }
        
        groupedData[product.ean].channels[product.desiredChannel] = product;
      });
      
      setGroupedProducts(Object.values(groupedData));
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (ean: string) => {
    setGroupedProducts(prev => 
      prev.map(group => 
        group.ean === ean ? { ...group, expanded: !group.expanded } : group
      )
    );
  };

  const handleStartEdit = (product: Product) => {
    setSelectedProduct(product);
    setNewMinPrice(product.proposedPrice.toString());
    setNewProposedPrice(product.proposedPrice.toString());
    setIsEditModalOpen(true);
  };

  const handleEditPrice = async () => {
    if (!selectedProduct || !newMinPrice || !newProposedPrice || 
        parseFloat(newMinPrice) <= 0 || parseFloat(newProposedPrice) <= 0) {
      toast.error('Por favor, insira preços válidos');
      return;
    }

    const newMinPriceFloat = Number(parseFloat(newMinPrice).toFixed(2));
    const newProposedPriceFloat = Number(parseFloat(newProposedPrice).toFixed(2));

    // Validar preço proposto contra ptPrice ou buyboxPrice
    const referencePrice = selectedProduct.ptPrice || selectedProduct.buyboxPrice;
    if (referencePrice && newProposedPriceFloat >= referencePrice) {
      toast.error(`O preço proposto deve ser menor que ${referencePrice.toFixed(2)}€ ${selectedProduct.ptPrice ? '(preço PT)' : '(preço buybox)'}`);
      return;
    }

    if (newProposedPriceFloat < newMinPriceFloat) {
      toast.error('O preço proposto não pode ser menor que o preço mínimo');
      return;
    }

    try {
      const { commission, iva, finalValue } = calculateCommission(newProposedPriceFloat);
      
      await updateProductPrice(selectedProduct.id, {
        minimumPrice: newMinPriceFloat,
        proposedPrice: newProposedPriceFloat,
        commission,
        valueToReceive: finalValue
      });
      
      toast.success('Preços atualizados com sucesso!');
      setUpdateSuccess(true);
      await loadProducts();
    } catch (error) {
      console.error('Erro ao atualizar preços:', error);
      toast.error('Erro ao atualizar preços');
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedProduct(null);
    setNewMinPrice('');
    setNewProposedPrice('');
    setUpdateSuccess(false);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedProduct(null);
  };

  const handleCloseCalculatorModal = () => {
    setIsCalculatorOpen(false);
    setSelectedProduct(null);
  };

  const getStatusBadge = (product: Product) => {
    if (product.status === 'approved_primary' || product.status === 'approved_reserve') {
      return (
        <div className="space-y-1">
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
            <span className="text-sm text-green-700">Aprovado</span>
          </div>
          <span className="block text-xs text-gray-500">
            {product.isActive === true ? (
              product.status === 'approved_primary'
                ? "Aprovado e publicado como vendedor principal"
                : "Aprovado e publicado como vendedor de reserva"
            ) : (
              "Aprovado mas aguarda publicação no marketplace"
            )}
          </span>
        </div>
      );
    } else {
      return (
        <div className="space-y-1">
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-yellow-500 mr-2"></div>
            <span className="text-sm text-yellow-700">Pendente</span>
          </div>
          <span className="block text-xs text-gray-500">
            {product.status === 'pending' && product.isActive === true
              ? "Aguarda aprovação da mudança de preço pela equipa de suporte (pode demorar até 24h úteis)"
              : "Pendente de intervenção da equipa de suporte"}
          </span>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Os Meus Produtos</h1>
          <p className="text-muted-foreground mt-1">
            Faça a gestão dos seus produtos nos marketplaces
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => setIsCalculatorOpen(true)}
            className="flex items-center gap-2"
          >
            <Calculator className="h-4 w-4" />
            Calculadora de Comissão
          </Button>
          <Link
            to="/dashboard/multicanal/products/submit"
            className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
          >
            Adicionar Produto
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Canal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço Min
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Publicado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {groupedProducts.map((group) => (
                <React.Fragment key={group.ean}>
                  <tr className="hover:bg-gray-50 border-b border-gray-100 cursor-pointer" onClick={() => toggleExpand(group.ean)}>
                    <td className="px-6 py-4 whitespace-nowrap" colSpan={8}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {group.productName}
                          </div>
                          <div className="text-sm text-gray-500">
                            EAN: {group.ean}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-500 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(group.ean);
                          }}
                        >
                          {group.expanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {group.expanded && Object.values(group.channels).map((product) => (
                    <tr key={product.id} className="bg-gray-50 border-b border-gray-100">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {/* Célula vazia para a coluna Produto */}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img 
                            src={getMarketplaceLogo(product.desiredChannel)} 
                            alt={product.desiredChannel} 
                            className="h-5 w-auto mr-2" 
                          />
                          <div className="text-sm font-medium text-gray-900">{product.desiredChannel}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(product.proposedPrice)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(product.minimumPrice || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{product.stockQuantity}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(product)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          {product.isActive === true ? (
                            <span className="flex items-center text-green-600">
                              <Check className="w-5 h-5" />
                            </span>
                          ) : (
                            <span className="flex items-center text-yellow-600">
                              <Clock className="w-5 h-5" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartEdit(product)}
                            className="flex items-center gap-2"
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedProduct(product);
                              setIsDeleteModalOpen(true);
                            }}
                            className="flex items-center gap-2 text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remover
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog.Root open={isCalculatorOpen} onOpenChange={handleCloseCalculatorModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[400px] space-y-4">
            <Dialog.Title className="text-lg font-semibold">
              Calculadora de Comissão
            </Dialog.Title>
            <div className="space-y-4">
              <div>
                <Label htmlFor="sellingPrice">Preço de Venda (€)</Label>
                <Input
                  id="sellingPrice"
                  type="number"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              {sellingPrice && parseFloat(sellingPrice) > 0 && (
                <div className="bg-primary/5 p-4 rounded-md space-y-2">
                  {(() => {
                    const price = parseFloat(sellingPrice);
                    const { commission, iva, finalValue } = calculateCommission(price);
                    return (
                      <>
                        <p className="flex justify-between">
                          <span>Comissão (20%):</span>
                          <span className="font-medium text-red-600">
                            -{commission.toFixed(2)}€
                          </span>
                        </p>
                        <p className="flex justify-between">
                          <span>IVA sobre comissão (23%):</span>
                          <span className="font-medium text-red-600">
                            -{iva.toFixed(2)}€
                          </span>
                        </p>
                        <div className="border-t pt-2 mt-2">
                          <p className="flex justify-between font-semibold">
                            <span>Valor a receber:</span>
                            <span className="text-green-600">{finalValue.toFixed(2)}€</span>
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={handleCloseCalculatorModal}
              >
                Fechar
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Modal de Edição */}
      <Dialog.Root open={isEditModalOpen} onOpenChange={handleCloseEditModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[400px] space-y-4">
            {!updateSuccess ? (
              <>
                <Dialog.Title className="text-lg font-semibold">
                  Editar Preços
                </Dialog.Title>
                <div className="space-y-4">
                  {selectedProduct && (
                    <div className="bg-primary/5 p-4 rounded-md space-y-2">
                      {selectedProduct.ptPrice ? (
                        <p className="text-sm text-muted-foreground flex justify-between">
                          <span>Preço em Portugal:</span>
                          <span className="font-medium text-primary">{selectedProduct.ptPrice.toFixed(2)}€</span>
                        </p>
                      ) : selectedProduct.buyboxPrice ? (
                        <p className="text-sm text-muted-foreground flex justify-between">
                          <span>Preço Buybox:</span>
                          <span className="font-medium text-primary">{selectedProduct.buyboxPrice.toFixed(2)}€</span>
                        </p>
                      ) : null}
                      <p className="text-sm text-muted-foreground flex justify-between">
                        <span>Preço proposto atual:</span>
                        <span className="font-medium">{selectedProduct.proposedPrice.toFixed(2)}€</span>
                      </p>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="newProposedPrice">Preço Proposto (€)</Label>
                    <Input
                      id="newProposedPrice"
                      type="number"
                      value={newProposedPrice}
                      onChange={(e) => setNewProposedPrice(e.target.value)}
                      placeholder="0.00"
                      className="mt-1"
                    />
                    {selectedProduct && parseFloat(newProposedPrice) > 0 && (
                      <div className="mt-2 text-sm">
                        {(() => {
                          const price = parseFloat(newProposedPrice);
                          const { commission, iva, finalValue } = calculateCommission(price);
                          return (
                            <>
                              <p className="text-muted-foreground flex justify-between">
                                <span>Comissão (20%):</span>
                                <span className="text-red-600">-{commission.toFixed(2)}€</span>
                              </p>
                              <p className="text-muted-foreground flex justify-between">
                                <span>IVA sobre comissão (23%):</span>
                                <span className="text-red-600">-{iva.toFixed(2)}€</span>
                              </p>
                              <p className="text-muted-foreground flex justify-between font-medium">
                                <span>Valor a receber:</span>
                                <span className="text-green-600">{finalValue.toFixed(2)}€</span>
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="newMinPrice">Preço Mínimo (€)</Label>
                    <Input
                      id="newMinPrice"
                      type="number"
                      value={newMinPrice}
                      onChange={(e) => setNewMinPrice(e.target.value)}
                      placeholder="0.00"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    variant="outline"
                    onClick={handleCloseEditModal}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleEditPrice}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Guardar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center py-4">
                  <h3 className="text-lg font-medium text-green-600 mb-4">Preços atualizados com sucesso!</h3>
                  <Button
                    onClick={handleCloseEditModal}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Fechar
                  </Button>
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Modal de Remoção */}
      <Dialog.Root open={isDeleteModalOpen} onOpenChange={handleCloseDeleteModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[400px] space-y-4">
            <Dialog.Title className="text-lg font-semibold">
              Remover Produto
            </Dialog.Title>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Para remover o produto <strong>{selectedProduct?.productName}</strong> (EAN: {selectedProduct?.ean}), siga os passos abaixo:
              </p>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-600">
                <li>Coloque o stock a zero na página do produto</li>
                <li>Solicite por ticket de suporte identificando o EAN do produto que pretende efetuar a remoção</li>
              </ol>
              <p className="text-sm text-gray-600 mt-2 bg-yellow-50 p-3 rounded-md border border-yellow-200">
                A remoção será efetuada num prazo máximo de 24h úteis.
              </p>
              <div className="flex justify-end gap-2 mt-6">
                <Button
                  onClick={handleCloseDeleteModal}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  Entendi
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
